import React, { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DetailedContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_interest: '',
    company_size: '',
    budget_range: '',
    timeline: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const serviceOptions = [
    'Managed IT Services',
    'Cybersecurity & Compliance',
    'Cloud & Microsoft 365',
    'Network Infrastructure',
    'Physical Security (CCTV/Access Control)',
    'vCIO / IT Advisory',
    'Multiple Services',
    'Not Sure - Need Assessment',
  ];

  const companySizeOptions = [
    '1-10 employees',
    '11-50 employees',
    '51-100 employees',
    '101-250 employees',
    '250+ employees',
  ];

  const budgetOptions = [
    'Under $1,000/month',
    '$1,000 - $5,000/month',
    '$5,000 - $10,000/month',
    '$10,000 - $25,000/month',
    '$25,000+/month',
    'Not Sure / Need Quote',
  ];

  const timelineOptions = [
    'Immediate (ASAP)',
    'Within 1 month',
    '1-3 months',
    '3-6 months',
    'Just exploring options',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/contact/detailed`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service_interest: '',
        company_size: '',
        budget_range: '',
        timeline: '',
        description: '',
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12 animate-fade-in" data-testid="detailed-form-success">
        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
        <h3 className="text-2xl font-bold font-heading text-slate-900 mb-3">Consultation Request Received!</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Thank you for your interest in mimoIT. Our team will review your requirements and contact you within one business day.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="mt-6 text-mimo-primary font-medium hover:underline"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="detailed-contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="d-name" className="block text-sm font-medium text-slate-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            id="d-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="input-field"
            placeholder="John Smith"
            data-testid="detailed-form-name"
          />
        </div>

        <div>
          <label htmlFor="d-email" className="block text-sm font-medium text-slate-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            id="d-email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-field"
            placeholder="john@company.com"
            data-testid="detailed-form-email"
          />
        </div>

        <div>
          <label htmlFor="d-phone" className="block text-sm font-medium text-slate-700 mb-1">
            Phone Number *
          </label>
          <input
            type="tel"
            id="d-phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="input-field"
            placeholder="(201) 555-0123"
            data-testid="detailed-form-phone"
          />
        </div>

        <div>
          <label htmlFor="d-company" className="block text-sm font-medium text-slate-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="d-company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="input-field"
            placeholder="Your Company Inc."
            data-testid="detailed-form-company"
          />
        </div>

        <div>
          <label htmlFor="d-service" className="block text-sm font-medium text-slate-700 mb-1">
            Service Interest *
          </label>
          <select
            id="d-service"
            name="service_interest"
            value={formData.service_interest}
            onChange={handleChange}
            required
            className="input-field"
            data-testid="detailed-form-service"
          >
            <option value="">Select a service</option>
            {serviceOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="d-size" className="block text-sm font-medium text-slate-700 mb-1">
            Company Size *
          </label>
          <select
            id="d-size"
            name="company_size"
            value={formData.company_size}
            onChange={handleChange}
            required
            className="input-field"
            data-testid="detailed-form-size"
          >
            <option value="">Select company size</option>
            {companySizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="d-budget" className="block text-sm font-medium text-slate-700 mb-1">
            Budget Range *
          </label>
          <select
            id="d-budget"
            name="budget_range"
            value={formData.budget_range}
            onChange={handleChange}
            required
            className="input-field"
            data-testid="detailed-form-budget"
          >
            <option value="">Select budget range</option>
            {budgetOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="d-timeline" className="block text-sm font-medium text-slate-700 mb-1">
            Timeline *
          </label>
          <select
            id="d-timeline"
            name="timeline"
            value={formData.timeline}
            onChange={handleChange}
            required
            className="input-field"
            data-testid="detailed-form-timeline"
          >
            <option value="">Select timeline</option>
            {timelineOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="d-description" className="block text-sm font-medium text-slate-700 mb-1">
          Describe Your IT Needs *
        </label>
        <textarea
          id="d-description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={5}
          className="input-field resize-none"
          placeholder="Please describe your current IT challenges, goals, and any specific requirements..."
          data-testid="detailed-form-description"
        />
      </div>

      {error && (
        <p className="text-red-500 text-sm" data-testid="detailed-form-error">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full flex items-center justify-center gap-2 py-4"
        data-testid="detailed-form-submit"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Submitting Request...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Request Consultation
          </>
        )}
      </button>
    </form>
  );
};

export default DetailedContactForm;